import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useParticipantAuth } from "@/context/ParticipantAuthContext";
import { apiErrorMessage } from "@/api/client";
import { ArrowRight, Loader2, Lock, User, Mail, BarChart3, Lightbulb, Users } from "lucide-react";

const HERO_IMAGE = "https://images.pexels.com/photos/18591087/pexels-photo-18591087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=900";

const FEATURES = [
  { icon: BarChart3, label: "EVALÚA TUS FORTALEZAS" },
  { icon: Lightbulb, label: "IDENTIFICA OPORTUNIDADES" },
  { icon: Users, label: "IMPULSA TU DESARROLLO" },
];

export default function ParticipantLogin() {
  const [rut, setRut] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useParticipantAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!rut.trim() || !email.trim()) {
      setError("Ingresa tu RUT y tu correo institucional.");
      return;
    }
    setLoading(true);
    try {
      await login(rut.trim(), email.trim());
      navigate("/portal");
    } catch (err) {
      setError(apiErrorMessage(err, "No fue posible iniciar sesión."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)] flex flex-col">
      {/* Header institucional */}
      <header className="px-5 sm:px-8 py-5 flex items-start justify-between gap-4" data-testid="brand-header">
        <div className="flex items-center gap-3">
          <img src="/brand/inside-logo.png" alt="INSIDE Training & Learning" className="h-8 sm:h-9 w-auto" />
          <div className="hidden sm:block w-px h-8 bg-slate-200" />
          <div className="hidden sm:block leading-tight">
            <p className="text-[11px] font-extrabold text-slate-800 tracking-wide">ANID</p>
            <p className="text-[9px] text-slate-400 tracking-wide">Agencia Nacional de Investigación y Desarrollo</p>
          </div>
        </div>
        <div className="text-right hidden md:block" data-testid="brand-claim">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400">Personas que impulsan un mejor futuro</p>
          <div className="h-[2px] w-16 bg-brand-coral mt-1.5 ml-auto" />
        </div>
        <Link to="/admin/login" data-testid="admin-access-link" className="text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition-colors shrink-0 flex items-center gap-1">
          <Lock className="w-3 h-3" /> Admin
        </Link>
      </header>

      <div className="text-center px-5 mb-6">
        <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-brand-accent">Curso de Habilidades para el Liderazgo</p>
        <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">ANID · Programa de Formación y Desarrollo de Competencias de Liderazgo</p>
        <h1 className="font-display text-xl sm:text-2xl font-extrabold text-[color:var(--brand-navy)] mt-2 tracking-tight">Diagnóstico Inicial de Competencias de Liderazgo</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1.5 max-w-md mx-auto">Este diagnóstico permitirá identificar fortalezas y oportunidades de desarrollo para orientar los contenidos y actividades del curso.</p>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 max-w-6xl w-full mx-auto">
        {/* Columna izquierda: mensaje editorial */}
        <div className="order-2 lg:order-1 px-6 sm:px-10 lg:px-14 py-8 lg:py-14 flex flex-col justify-center">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[color:var(--brand-navy)] leading-tight">
            Liderazgo que genera <span className="text-brand-accent">más impacto</span>
          </h2>
          <div className="w-10 h-[3px] bg-brand-accent mt-4 mb-4" />
          <p className="text-sm text-slate-600 leading-relaxed max-w-md">
            Diagnóstico inicial de competencias de liderazgo para jefaturas intermedias de ANID.
            Tus resultados orientarán el trabajo formativo del programa y tienen carácter estrictamente formativo.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 max-w-md">
            {FEATURES.map((f) => (
              <div key={f.label} className="flex flex-col items-start gap-2">
                <div className="w-10 h-10 rounded-full bg-[color:var(--brand-sky-tint)] flex items-center justify-center">
                  <f.icon className="text-brand-accent" style={{ width: 18, height: 18 }} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500 leading-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Columna derecha: foto + card flotante de acceso */}
        <div className="order-1 lg:order-2 relative min-h-[380px] lg:min-h-0 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--brand-navy)]/40 via-transparent to-transparent lg:bg-gradient-to-l lg:from-transparent lg:via-transparent lg:to-[color:var(--brand-bg)]/10" />
          <span className="hidden lg:block absolute right-4 top-1/2 -translate-y-1/2 rotate-180 [writing-mode:vertical-rl] text-white/85 text-[10px] tracking-[0.28em] font-semibold">
            TALENTO PARA UNA CIENCIA QUE TRANSFORMA
          </span>

          <div className="relative h-full flex items-center justify-center p-5 lg:p-10">
            <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200/70 shadow-[0_18px_45px_rgba(15,23,42,0.22)] p-7">
              <div className="w-11 h-11 rounded-full bg-[color:var(--brand-sky-tint)] flex items-center justify-center mb-4">
                <Users className="w-5 h-5 text-brand-accent" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Acceso participante</h3>
              <p className="text-xs text-slate-500 mb-5">Ingresa con tu RUT y tu correo institucional registrados en la matrícula del programa.</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="participant-login-form">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">RUT</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="participant-login-rut"
                      type="text"
                      placeholder="12.345.678-5"
                      value={rut}
                      onChange={(e) => setRut(e.target.value)}
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-inside-blue)] focus-visible:ring-offset-2 focus:border-brand-accent bg-slate-50 text-sm font-medium transition-shadow"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Correo institucional</label>
                  <div className="relative">
                    <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                    <input
                      data-testid="participant-login-email"
                      type="email"
                      placeholder="nombre@institucion.cl"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-11 pl-10 pr-4 rounded-xl border border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-inside-blue)] focus-visible:ring-offset-2 focus:border-brand-accent bg-slate-50 text-sm font-medium transition-shadow"
                    />
                  </div>
                </div>
                {error && (
                  <div data-testid="participant-login-error" className="text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">{error}</div>
                )}
                <button
                  data-testid="participant-login-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 px-6 bg-[color:var(--brand-inside-blue)] hover:bg-[color:var(--brand-inside-blue-2)] text-white font-bold rounded-xl shadow-md flex items-center justify-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:pointer-events-none active:scale-[0.99]"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Ingresar <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>

              <div className="mt-5 pt-4 border-t border-slate-100 text-center">
                <Link to="/admin/login" data-testid="participant-login-admin-link" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition-colors">
                  <Lock className="w-3 h-3" /> Acceso administrador INSIDE
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-[color:var(--brand-navy)] text-white px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-[10px] font-semibold text-slate-300">Herramienta de apoyo al programa desarrollada por INSIDE® Training &amp; Learning</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Liderazgo hoy para grandes mañanas</p>
      </footer>
    </div>
  );
}
